//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { configService, datasetService } from '@/services';
import {
  type ConfigXgt, type DatasetInfo, type MemoryData
} from '@/services/apiTypes';

// Query keys and query options for the xGT server-state resources (config,
// memory, datasets).  Shared so the useQuery hooks and any imperative
// queryClient.fetchQuery/invalidateQueries calls use byte-identical config.
// There is no schema key: each dataset's schema rides inside the datasets
// payload (graph_schema), so the schema views derive from the datasets query
// and can never be stale relative to it.

export const xgtKeys = {
  all: ['xgt'] as const,
  memory: () => [...xgtKeys.all, 'memory'] as const,
  config: () => [...xgtKeys.all, 'config'] as const,
  datasets: () => [...xgtKeys.all, 'datasets'] as const,
};

// memory and datasets are automated freshness polls, never user actions, so
// their fetches are tagged as background — they keep the data current without
// resetting the backend inactivity timeout.
export const memoryQuery = () => ({
  queryKey: xgtKeys.memory(),
  queryFn: (): Promise<MemoryData> => configService.getMemory(true),
});

export const configQuery = () => ({
  queryKey: xgtKeys.config(),
  queryFn: (): Promise<ConfigXgt> => configService.getConfigXgt(),
});

export const datasetsQuery = () => ({
  queryKey: xgtKeys.datasets(),
  queryFn: (): Promise<DatasetInfo[]> =>
    datasetService.getDatasetsInfo(undefined, true),
});

// Single owner of the datasets-invalidation: returns a stable callback that
// marks the datasets query stale so it refetches.  Used by the async-job
// writes (createDataset, demo-load, upload, query execution) that can't seed
// the cache because they return a job handle rather than the new list, and by
// the navigation invalidation (fresh data on arrival at any tab).  Call sites
// use this instead of referencing xgtKeys.datasets() directly, so the key and
// the invalidation semantics live in one place.  Because each dataset's
// schema is embedded in the payload, this is also the only schema-freshness
// mechanism.
export const useInvalidateDatasets = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: xgtKeys.datasets() }),
    [queryClient]);
};

// The datasets list as the cache holds it this instant, or undefined before
// the first fetch.  For a reader that cannot wait for the render that would
// hand it the list as a prop: a refetch lands in the cache first, and the
// render that shows it comes a tick later.
export const useDatasetsCache = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.getQueryData<DatasetInfo[]>(xgtKeys.datasets()),
    [queryClient]);
};
